import React, { useState } from 'react';
import { InventoryDashboard } from '../../types/inventory';

interface Props {
  dashboard: InventoryDashboard | null;
}

const StatCard: React.FC<{ label: string; value: number | string; color?: string; onClick?: () => void }> = ({ label, value, color, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: '10px 16px',
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--card-border)',
      borderRadius: 8,
      minWidth: 120,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 200ms ease',
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = '#EF4444'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; }}
  >
    <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 600, color: color || 'var(--text-primary)', marginTop: 2 }}>
      {value}
    </div>
  </div>
);

const InventoryDashboardStats: React.FC<Props> = ({ dashboard }) => {
  const [showAlerts, setShowAlerts] = useState(false);

  if (!dashboard) return null;

  const lowStock = dashboard.low_stock_alerts || [];
  const expiring = dashboard.expiring_soon || [];
  const alertCount = lowStock.length + expiring.length;

  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{
        display: 'flex',
        gap: 12,
        padding: '12px 16px',
        overflowX: 'auto',
      }}>
        <StatCard label="Compounds" value={dashboard.compound_count} />
        <StatCard label="Batches" value={dashboard.batch_count} />
        <StatCard label="Samples" value={dashboard.sample_count} />
        <StatCard label="Available" value={dashboard.available_samples} color="#0D9488" />
        {alertCount > 0 && (
          <StatCard label="Alerts" value={alertCount} color="#EF4444" onClick={() => setShowAlerts(!showAlerts)} />
        )}
        {(dashboard.by_ip_status?.blocked || 0) > 0 && (
          <StatCard label="IP Blocked" value={dashboard.by_ip_status.blocked} color="#EF4444" />
        )}
        {(dashboard.by_ip_status?.caution || 0) > 0 && (
          <StatCard label="IP Caution" value={dashboard.by_ip_status.caution} color="#F59E0B" />
        )}
      </div>

      {/* Alert detail panel */}
      {showAlerts && alertCount > 0 && (
        <div style={{
          margin: '0 16px 8px', padding: 12, borderRadius: 8,
          backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Alerts ({alertCount})</span>
            <button onClick={() => setShowAlerts(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14 }}>{'\u00D7'}</button>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lowStock.map((a: any, i: number) => (
              <div key={`ls-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, backgroundColor: 'var(--content-bg)' }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>LOW STOCK</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', flex: 1 }}>{a.compound_name || a.chemistry_code}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.total_stock != null ? `${Number(a.total_stock).toFixed(1)} mg` : ''}</span>
              </div>
            ))}
            {expiring.map((a: any, i: number) => (
              <div key={`ex-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, backgroundColor: 'var(--content-bg)' }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, backgroundColor: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>EXPIRING</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', flex: 1 }}>{a.compound_name || a.chemistry_code || a.barcode}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.expiry_date || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDashboardStats;
